// PageSidenav.jsx
import React, { useState } from "react";
import { Layout, Menu, Tooltip } from "antd";
import { Link, useLocation } from "react-router-dom";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import {
  FileTextOutlined,
  TeamOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { FamilyRestroom } from "@mui/icons-material";
import { useSubscriptions } from "../../contexts/subscriptionsContext";
import { Menu as MenuIcon, X as CloseIcon, UserIcon } from "lucide-react"; // menu toggle icons
import { getFreeTrialDaysLeft } from "../../helper/getFreeTrialsDayLeft";
import { hasFreeTrialExpired } from "../../helper/hasFreeTrialExpired";
import { useUserInfo } from "../../contexts/userInfo";

const { Sider } = Layout;

export const PageSidenav = () => {
  const { userInfo } = useUserInfo();
  const location = useLocation();
  const isInvitedMember =
    !Array.isArray(userInfo?.inviter) &&
    userInfo?.inviter?.has_calendar_access == 1;
  const { purchasedSubscription, isCoParent } = useSubscriptions();
  const [isOpen, setIsOpen] = useState(false);

  const createdAt = userInfo?.created_at;

  // trial values (use helpers)
  const trialDaysLeft = createdAt ? getFreeTrialDaysLeft(createdAt) : 0;
  const trialExpired = createdAt ? hasFreeTrialExpired(createdAt) : true;

  const purchasedServiceNames =
    purchasedSubscription?.services?.map((s) => s.name) || [];

  const menuItems = [
    {
      key: "/dashboard",
      icon: <AssignmentIcon style={{ fontSize: "18px" }} />,
      label: <Link to="/dashboard">Dashboard</Link>,
      requiredService: null, // always visible
      name: "Dashboard",
    },
    {
      key: "/notes",
      icon: <FileTextOutlined style={{ fontSize: "18px" }} />,
      label: <Link to="/notes">Notes</Link>,
      requiredService: "Shared Notes & Notifications",
      name: "Notes",
    },
    {
      key: "/calender",
      icon: <CalendarMonthOutlined style={{ fontSize: "18px" }} />,
      label: <Link to="/calender">Calendar</Link>,
      requiredService: "Personal Event Reminders & Notes (MVP version)",
      name: "Family Calendar",
    },
    {
      key: "/members",
      icon: <TeamOutlined style={{ fontSize: "18px" }} />,
      label: <Link to="/members">Members</Link>,
      requiredService: "Shared Calendar with Children/Relatives",
      name: "Members",
    },
    {
      key: "/invite-capture",
      icon: <TeamOutlined style={{ fontSize: "18px" }} />,
      label: <Link to="/invite-capture">Invite Capture</Link>,
      requiredService: "Invite Capture (photo/PDF to calendar)",
      name: " Invite Capture",
    },
    {
      key: "/emailListing",
      icon: <MailOutlined style={{ fontSize: "18px" }} />,
      label: <Link to="/emailListing">Email Assistant</Link>,
      requiredService: "AI-Powered Email Assistant",
      name: "Email Assistent",
    },
    {
      key: "/co-parent",
      icon: <FamilyRestroom style={{ fontSize: "18px" }} />,
      label: <Link to="/co-parent">Co Parent</Link>,
      requiredService:
        "Co-Parenting Calendar (shared child \narrangement tools)",
      name: "Co-parent",
    },
    {
      key: "/profile",
      icon: <UserOutlined style={{ fontSize: "18px" }} />,
      label: <Link to="/profile">Profile</Link>,
      requiredService: null,
      name: "Profile",
    },
  ];

  // const filteredMenu = menuItems.filter((item) => {
  //   if (!item.requiredService) return true;
  //   return purchasedServiceNames.includes(item.requiredService);
  // });

  return (
    <>
      {/* Mobile toggle button */}
      <div className="fixed top-4 left-4 z-50 md:hidden">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 rounded bg-purple-100 text-purple-700"
        >
          {isOpen ? <CloseIcon size={20} /> : <MenuIcon size={20} />}
        </button>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 transform transition-transform duration-300
        ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0 md:static md:shadow-none h-[-webkit-fill-available]`}
        style={{
          transitionProperty: "transform",
          height: isOpen ? "" : "auto",
          overflowY: "auto",
        }}
      >
        {/* Logo */}
        <div className="flex justify-center py-6">
          <img src="/quelld-signup.png" className="w-24" alt="Quelld Logo" />
        </div>

        {/* Menu */}
        <nav className="mt-10 px-6 nav-bar">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.key;
            const isTrialActive = !trialExpired && trialDaysLeft > 0;
            const hasPlanAccess =
              !item.requiredService ||
              purchasedServiceNames.some(
                (serviceName) => serviceName === item.requiredService
              );
            const haveActiveSubscription = purchasedServiceNames.length > 0;

            const isDashboardOrProfile =
              item.key === "/dashboard" || item.key === "/profile";

            const isCoParentAllowed =
              isCoParent &&
              item.requiredService ===
                "Co-Parenting Calendar (shared child \narrangement tools)";

            const isCalendarAllowed =
              isInvitedMember &&
              item.requiredService ===
                "Personal Event Reminders & Notes (MVP version)";

            const isAvailable =
              isDashboardOrProfile || isCoParentAllowed || isCalendarAllowed
                ? true
                : haveActiveSubscription
                ? hasPlanAccess
                : isTrialActive;

            const linkElement = (
              <Link
                key={item.key}
                to={isAvailable ? item.key : "#"}
                className={`flex items-center gap-3 py-3 px-4 mb-2 rounded-lg transition-colors
          ${
            isActive && isAvailable
              ? "bg-purple-100 text-purple-700 font-semibold"
              : isAvailable
              ? "text-gray-700 hover:bg-purple-50"
              : "text-gray-400 cursor-not-allowed opacity-60"
          }`}
                onClick={(e) => {
                  if (!isAvailable) e.preventDefault(); // disable click
                  setIsOpen(false);
                }}
              >
                <span className="text-lg">{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            );
            return isAvailable ? (
              linkElement
            ) : (
              <Tooltip
                key={item.key}
                title={
                  purchasedSubscription
                    ? `Not available in your plan`
                    : `You have no active plans to access ${item.name}`
                }
                placement="right"
              >
                {linkElement}
              </Tooltip>
            );
          })}
        </nav>
      </div>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-white bg-opacity-20 z-30 md:hidden"
          onClick={() => setIsOpen(false)}
        ></div>
      )}
    </>
  );
};
